import { Box, Button, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { generate } from "library/guid"
import Iconify from "minimals-template/components/Iconify"
import { ConditionalCardWrapper } from "./conditional-card-wrapper"

/**
 * Section that owns the draft list of conditional trades for one skill map line.
 *
 * Parents render this inside the `ConfigureSkillRoutingDialog` after the default-trade
 * selector. The collapsed label on the dialog header reflects the same state described
 * in the brief: "Maps to [Trade]" with no conditionals, "Maps to [Trade] · N conditions"
 * otherwise; the parent dialog handles the header text and uses this section as the
 * editable body.
 *
 * @param {object} props
 * @param {Array} props.conditionals - draft list owned by parent
 * @param {Array<[string,string]>} props.tradeOptions
 * @param {Map<string, string[]>} props.problemsById - validation messages keyed by conditional id
 * @param {function} props.onConditionalsChange - (nextConditionals) => void
 */
export function ConditionalTradesSection({ conditionals, tradeOptions, problemsById, onConditionalsChange }) {
    return (
        <Stack spacing={1.5} data-cy="conditional-trades-section">
            <Typography variant="subtitle2">Conditional trade assignments</Typography>
            <If value={conditionals.length === 0}>
                <Box sx={{ py: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        No conditionals configured. Tasks using this skill will be routed to the default trade.
                    </Typography>
                </Box>
            </If>
            <Repeat
                list={conditionals}
                item={
                    <ConditionalCardWrapper
                        all={conditionals}
                        tradeOptions={tradeOptions}
                        problemsById={problemsById}
                        onConditionalsChange={onConditionalsChange}
                    />
                }
                keyFn={(c, i) => c.id || `i:${i}`}
            />
            <Box>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Iconify icon="mdi:plus" />}
                    onClick={handleAdd}
                    data-cy="add-conditional-trade"
                >
                    Add conditional
                </Button>
            </Box>
        </Stack>
    )

    function handleAdd() {
        const newConditional = {
            id: generate(),
            name: "",
            conditions: [{ _id: generate(), field: "", operator: "", value: null }],
            combinator: "and",
            tradeId: null,
            sortOrder: conditionals.length,
        }
        onConditionalsChange([...conditionals, newConditional])
    }
}
